import { DictionaryPhrases, GetStaticComponentProps, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import { useI18n } from 'next-localization';
import React, { useEffect, useState, createContext } from 'react';

import { ButtonVariant } from 'components/helpers/Button/Button';
import Link from 'components/helpers/Link/Link';
import { BREAKPOINTS } from 'models/breakpoints';

import TertiaryNav from './TertiaryNav';
import LogoLink from './LogoLink';
import HomeLink from './HomeLink';
import LanguageSwitcher from './LanguageSwitcher';
import UtilityLink from './UtilityLink';
import MenuButton from './MenuButton';
import PrimaryNav from './PrimaryNav';
import SecondaryNav from './SecondaryNav';

import styles from './Header.module.scss';
import { dictionaryService } from 'scripts/layout-data-helper';
import { isNullOrEmptyObject } from 'lib/utils';
import { FlyOut, HeaderProps, UtilityLinkItem, contextType } from './HeaderTypes';
import SearchIcon from './SearchIcon';

const cx = classNames.bind(styles);

export const HeaderContext = createContext<contextType | null>(null);

const Header = (props: HeaderProps): React.JSX.Element => {
  const [openMenu, setOpenMenu] = useState(false);
  const [closeMenu, setCloseMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openSubNav, setOpenSubNav] = useState<string | null>('');
  const [openFlyout, setShowFlyout] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { fields } = props;

  const { currentLanguage, pageLanguages, showSearchIcon, searchLink, searchLinkParam } = fields;
  const currentLang = fields.currentLanguage;
  const currentPage = fields.currentPage;
  const datasourceLanguage = fields.datasourceLanguage;
  const { t } = useI18n();
  const navId = 'header-primary-nav';
  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing } = sitecoreContext;

  const $displaySearchIcon = showSearchIcon?.value && searchLink.value?.href;

  const handleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
    const relatedTarget = e.relatedTarget as HTMLElement;

    if (!relatedTarget?.classList?.contains('primary') && relatedTarget?.dataset?.id !== 'sublink') {
      setOpenSubNav(null);
    }

    setShowFlyout(false);
  };

  const resetMobileMenu = () => {
    document.body.classList.remove('no-scroll');
    setOpenMenu(false);
    setCloseMenu(true);
  };

  useEffect(() => {
    // Proceed to execute useEffect only when navigator.language is available?
    const mql: MediaQueryList = window.matchMedia(`(max-width: ${BREAKPOINTS.MOBILE_MAX}px)`);

    mql.addEventListener('change', (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      setHidden(e.matches);
      if (!e.matches) {
        resetMobileMenu();
      }
    });

    setIsMobile(window.innerWidth <= BREAKPOINTS.MOBILE_MAX);
  }, [openMenu]);

  if (isNullOrEmptyObject(fields)) {
    if (pageEditing) {
      return (
        <header className={cx('header', 'header__missing')}>
          <div className="container">
            <div className="row">
              <div className="col">
                <p>{t('Missing-Header-Datasource')}</p>
              </div>
            </div>
          </div>
        </header>
      );
    } else {
      return <></>;
    }
  }

  return (
    <HeaderContext.Provider
      value={{
        currentPage,
        currentLang,
        datasourceLanguage,
        handleBlur,
        resetMobileMenu,
        setIsMobile,
        isMobile,
        setOpenMenu,
        openMenu,
        setCloseMenu,
        closeMenu,
        setOpenSubNav,
        openSubNav,
        setShowFlyout,
        openFlyout,
        hidden,
        setHidden,
      }}
    >
      <header className={cx('header')}>
        <Link
          className={cx('skip-to-content')}
          field={{ value: { href: '#main' } }}
          showLinkTextWithChildrenPresent={false}
          variant={ButtonVariant.Primary}
          onClick={() => {
            if (openMenu) {
              resetMobileMenu();
              setCloseMenu(true);
            }
          }}
        >
          {t('SkipToContent-Label')}
        </Link>
        <div className={cx('header__divider')}>
          <div className={cx('container', 'header__divider--wrapper')}>
            <div className={cx('header__branding')}>
              <LogoLink logo={fields.firstLogo} link={fields.firstLogoLink} />
              <LogoLink logo={fields.secondLogo} link={fields.secondLogoLink} />
            </div>
            <div className={cx('header__secondary-nav-wrapper')}>
              {isMobile ? (
                <>
                  <LanguageSwitcher
                    isMobile={isMobile}
                    currentLanguage={currentLanguage}
                    currentPage={currentPage}
                    pageLanguages={pageLanguages}
                  />
                  {$displaySearchIcon && (
                    <SearchIcon
                      isMobile={isMobile}
                      pageLanguages={pageLanguages}
                      searchPageUrl={searchLink.value.href}
                      searchLinkParam={`${searchLinkParam.value}`}
                    />
                  )}
                </>
              ) : null}
              <TertiaryNav>
                {!isMobile && !fields.homeLink?.value.href && !fields.homeLinkText?.value && (
                  <HomeLink link={fields.homeLink} linkText={fields.homeLinkText} />
                )}

                {!isMobile && pageLanguages && pageLanguages.length > 1 && LanguageSwitcher !== null && (
                  <LanguageSwitcher
                    isMobile={isMobile}
                    currentLanguage={currentLanguage}
                    currentPage={currentPage}
                    pageLanguages={pageLanguages}
                  />
                )}

                {!isMobile && $displaySearchIcon && (
                  <SearchIcon
                    isMobile={isMobile}
                    pageLanguages={pageLanguages}
                    searchPageUrl={searchLink.value.href}
                    searchLinkParam={`${searchLinkParam.value}`}
                  />
                )}
              </TertiaryNav>

              <SecondaryNav utilitySection={fields.utilitySection}>
                {fields.utilitySection?.utilityLinks.length || fields.primaryNav?.navSections.length ? (
                  <MenuButton id={navId} />
                ) : null}
              </SecondaryNav>
            </div>
          </div>
        </div>
        <PrimaryNav navItem={fields.primaryNav} id={navId}>
          {fields.utilitySection?.utilityLinks.map((link, index) => {
            if ((link as UtilityLinkItem)?.link) {
              const utilityLinkItem: UtilityLinkItem = link as UtilityLinkItem;
              return <UtilityLink key={index} index={index} linkItem={utilityLinkItem} />;
            } else if (isMobile) {
              const utilityFlyOut: FlyOut = link as FlyOut;
              const flyOutAsUtility: UtilityLinkItem = {
                link: utilityFlyOut.mobileDestination,
                linkText: utilityFlyOut.mobileDestinationLabel,
              };
              flyOutAsUtility.link = utilityFlyOut.mobileDestination;
              return <UtilityLink key={index} index={index} linkItem={flyOutAsUtility} />;
            } //NavFlyOuts should only show in Utility Links in Mobile
            else {
              return null;
            }
          })}
          {isMobile && fields.homeLink.value.href != '' && fields.homeLinkText.value != '' ? (
            <HomeLink link={fields.homeLink} linkText={fields.homeLinkText} />
          ) : null}
        </PrimaryNav>
      </header>
    </HeaderContext.Provider>
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering, _layoutData, _context) => {
  let data: DictionaryPhrases | null = null;
  const props = rendering as unknown as HeaderProps;

  if (props.fields && props.fields.availableTranslations) {
    try {
      data = await dictionaryService.fetchDictionaryData(props.fields.availableTranslations.split(',')[0] ?? 'en');
    } catch (e) {
      console.log(e);
    }
  }

  return {
    translationText: data && data['Translation-Link'] ? data['Translation-Link'] : '',
  };
};

export default Header;
export * from './HeaderTypes';
